import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCompany } from '../contexts/CompanyContext';
import { useAuth } from '../contexts/AuthContext';

const MOBILE_BREAKPOINT = 900;

const NAV_LINKS = [
  { to: '/work', label: 'Главная' },
  { to: '/companies', label: 'Контрагенты' },
  { to: '/departments', label: 'Структурные подразделения' },
  { to: '/employees', label: 'Сотрудники' },
  { to: '/jobs', label: 'Должности' },
  { to: '/probation-instructions', label: 'Стажировка' },
];

const INSTRUCTION_LINKS = [
  { to: '/instructions', label: 'Вводный инструктаж' },
  { to: '/fire-instructions', label: 'Противопожарный инструктаж' },
  { to: '/labor-safety-instructions', label: 'Инструктаж по охране труда' },
];

export const Navigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCompany, companies, setSelectedCompany, loading } = useCompany();
  const { logout } = useAuth();
  const [showInstructionsDropdown, setShowInstructionsDropdown] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );

  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Закрываем мобильное меню при переходе на другую страницу
  React.useEffect(() => {
    setMobileMenuOpen(false);
    setShowInstructionsDropdown(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;
  const isInstructionsActive = INSTRUCTION_LINKS.some(link => isActive(link.to));

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const linkStyle = (active: boolean): React.CSSProperties => ({
    textDecoration: 'none',
    color: active ? '#007bff' : '#666',
    fontWeight: active ? 600 : 400,
    padding: '8px 12px',
    borderRadius: '4px',
    transition: 'all 0.2s',
    display: 'block',
    whiteSpace: 'nowrap',
  });

  // Ссылки навигации (используются и в десктопном, и в мобильном меню)
  const navLinksContent = (
    <>
      {NAV_LINKS.map(link => (
        <Link
          key={link.to}
          to={link.to}
          style={linkStyle(isActive(link.to))}
          onClick={closeMobileMenu}
        >
          {link.label}
        </Link>
      ))}

      <div style={{ position: 'relative', display: isMobile ? 'block' : 'inline-block' }}>
        <button
          style={{
            background: 'none',
            border: 'none',
            color: isInstructionsActive ? '#007bff' : '#666',
            fontWeight: isInstructionsActive ? 600 : 400,
            padding: '8px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            font: 'inherit',
            textAlign: 'left',
            width: isMobile ? '100%' : 'auto',
            whiteSpace: 'nowrap',
          }}
          onClick={() => setShowInstructionsDropdown(!showInstructionsDropdown)}
        >
          Инструктаж ▼
        </button>
        {showInstructionsDropdown && (
          <div
            style={
              isMobile
                ? { paddingLeft: '16px' }
                : {
                    position: 'absolute',
                    top: '100%',
                    // Выпадающий список прижат к правому краю кнопки, чтобы не уезжать за экран
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    minWidth: '240px',
                  }
            }
          >
            {INSTRUCTION_LINKS.map((link, index) => (
              <Link
                key={link.to}
                to={link.to}
                style={{
                  display: 'block',
                  padding: '10px 16px',
                  textDecoration: 'none',
                  color: isActive(link.to) ? '#007bff' : '#666',
                  borderBottom: index < INSTRUCTION_LINKS.length - 1 ? '1px solid #eee' : undefined,
                }}
                onClick={() => {
                  setShowInstructionsDropdown(false);
                  closeMobileMenu();
                }}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );

  return (
    <nav style={{
      backgroundColor: '#fff',
      borderBottom: '1px solid #ddd',
      padding: '0 20px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Верхняя строка: логотип, выбор контрагента и выход */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: isMobile ? '10px' : '0',
          minHeight: '60px'
        }}>
          <Link to="/work" style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#333',
            textDecoration: 'none',
            marginRight: isMobile ? 'auto' : '40px'
          }}>
            Safety App
          </Link>

          {/* Бургер-кнопка (только на мобильных) */}
          {isMobile && (
            <button
              aria-label="Меню"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                background: 'none',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '20px',
                lineHeight: 1,
                color: '#333',
              }}
            >
              {mobileMenuOpen ? '✕' : '☰'}
            </button>
          )}

          {/* Выбор контрагента */}
          <div style={{
            marginBottom: isMobile ? '8px' : '0',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexBasis: isMobile ? '100%' : 'auto',
            order: isMobile ? 3 : 0,
          }}>
            <label style={{
              fontSize: '14px',
              color: '#666',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}>
              Контрагент:
            </label>
            <select
              value={selectedCompany?.id || ''}
              onChange={(e) => {
                const companyId = parseInt(e.target.value);
                const company = companies.find(c => c.id === companyId) || null;
                setSelectedCompany(company);
              }}
              disabled={loading}
              style={{
                padding: '6px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#fff',
                fontSize: '14px',
                minWidth: isMobile ? '0' : '200px',
                flex: isMobile ? 1 : undefined,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? (
                <option>Загрузка...</option>
              ) : companies.length === 0 ? (
                <option>Контрагенты не найдены</option>
              ) : (
                companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.shortName || company.fullName}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Выход (на десктопе — в верхней строке справа) */}
          {!isMobile && (
            <button
              onClick={handleLogout}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: '1px solid #ddd',
                color: '#666',
                padding: '6px 14px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
            >
              Выход
            </button>
          )}
        </div>

        {/* Нижняя строка: меню (только на десктопе) */}
        {!isMobile && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            columnGap: '12px',
            rowGap: '4px',
            alignItems: 'center',
            borderTop: '1px solid #eee',
            paddingTop: '6px',
            paddingBottom: '6px',
          }}>
            {navLinksContent}
          </div>
        )}

        {/* Мобильное меню (раскрывающееся) */}
        {isMobile && mobileMenuOpen && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            paddingBottom: '12px',
            borderTop: '1px solid #eee',
            paddingTop: '8px',
          }}>
            {navLinksContent}
            <button
              onClick={handleLogout}
              style={{
                marginTop: '8px',
                background: 'none',
                border: '1px solid #ddd',
                color: '#666',
                padding: '10px 14px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s',
                textAlign: 'center',
              }}
            >
              Выход
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};
